import type { Destroy, GetState, SetState, Subscribe } from 'zustand/vanilla';
import create from 'zustand/vanilla';
import reactiveCreate from 'zustand';
import type { GettingStartedGuideState } from '@hotjar/webapp/features/Activation/GetStartedGuide/types';
import { StepId } from '@hotjar/webapp/features/Activation/GetStartedGuide/types';
import type { Site } from '@hotjar/site';
import { get as getAppState, set as setAppState } from '@hotjar/webapp/domains/common/app-state';
import * as trackingCode from '@hotjar/webapp/domains/activation/tracking-code';
import type { RawGettingStartedGuideState, CachedState } from './types';
import { GettingStartedGuideStateEnum } from './types';

type GettingStartedGuideStatePerSite = {
  [key: number]: RawGettingStartedGuideState;
};
interface Store {
  getState: GetState<GettingStartedGuideStatePerSite>;
  setState: SetState<GettingStartedGuideStatePerSite>;
  subscribe: Subscribe<GettingStartedGuideStatePerSite>;
  destroy: Destroy;
}

export type UserId = number;

export const store: Store = create(() => ({}));

export const useGSGStore = reactiveCreate(store);

const NAMESPACE = 'atv_getting_started_guide';
// We want to show the getting started guided for new sites only,
// so we chose to hardcode the following timestamp that should be
// the date we actually deployed this change to production
const SITE_CREATED_TIMESTAMP = 1604335671; // November 2, 2020

export const DEFAULT_STATE = {
  enabled: false,
  hasLoaded: true,
};

const WRITE_ACCESS_LEVELS = ['write', 'admin'];

const hasWritePermissions = (site: Site) =>
  WRITE_ACCESS_LEVELS.includes(site?.organization?.access);

const fetchState = async (site: Site, userId: UserId): Promise<RawGettingStartedGuideState> => {
  const appStateSettings = await getAppState(userId, { siteId: site.id });

  const newState = appStateSettings[NAMESPACE]
    ? (Object.entries(appStateSettings[NAMESPACE]).reduce(
        (state, [key, value]) => ({
          ...state,
          [key]: value === 'True',
        }),
        {},
      ) as RawGettingStartedGuideState)
    : {};

  // we cache the promise to make sure we only request once per site
  store.setState({
    [site.id]: newState,
  });
  return store.getState()[site.id];
};

export const transformState = (
  state: RawGettingStartedGuideState,
  site: Site,
): GettingStartedGuideState => ({
  enabled:
    !state[GettingStartedGuideStateEnum.COMPLETED_KEY] &&
    !state[GettingStartedGuideStateEnum.SKIPPED_KEY],
  hasLoaded: true,
  site,
  spacesStepCompleted: state[GettingStartedGuideStateEnum.SPACES_STEP_COMPLETED_KEY],
  inviteCompleted: state[GettingStartedGuideStateEnum.INVITE_COMPLETED_KEY],
  welcomeVideoSeen: state[GettingStartedGuideStateEnum.SAW_WELCOME_VIDEO],
  isSpaceEnabled: hasWritePermissions(site),
  isInviteEnabled: hasWritePermissions(site),
  trackingCodeInstalled: trackingCode.getState(site.lastData).installed,
  isCelebrated: !!state[GettingStartedGuideStateEnum.CELEBRATED_KEY],
  recordingViewed: state[GettingStartedGuideStateEnum.VIEWED_RECORDING_KEY],
  heatmapViewed: state[GettingStartedGuideStateEnum.VIEWED_HEATMAP_KEY],
  incomingFeedbackCreated: state[GettingStartedGuideStateEnum.CREATED_INCOMING_FEEDBACK_KEY],
  surveyLaunched: state[GettingStartedGuideStateEnum.LAUNCHED_SURVEY_KEY],
  isFeedbackEnabled: hasWritePermissions(site),
  isHighlightsViewed: state[GettingStartedGuideStateEnum.HIGHLIGHTS_VIEWED],
  isIntegrationsViewed: state[GettingStartedGuideStateEnum.INTEGRATIONS_VIEWED],
  isSegmentsViewed: state[GettingStartedGuideStateEnum.SEGMENTS_VIEWED],
  isGlobalFiltersViewed: state[GettingStartedGuideStateEnum.GLOBAL_FILTERS_VIEWED],
  isLocalFiltersViewed: state[GettingStartedGuideStateEnum.LOCAL_FILTERS_VIEWED],
});

export const selectStoreById = (
  state: CachedState,
  site?: Site | undefined,
): GettingStartedGuideState => {
  if (site && state[site.id]) {
    return transformState(state[site.id], site);
  }

  if (site) {
    return {
      ...DEFAULT_STATE,
      hasLoaded: false,
      site,
      isInviteEnabled: hasWritePermissions(site),
      trackingCodeInstalled: trackingCode.getState(site.lastData).installed,
    };
  }

  return DEFAULT_STATE;
};

const getRawState = (site: Site, userId: UserId): Promise<RawGettingStartedGuideState> => {
  const cached = store.getState()[site.id];
  if (cached && Object.keys(cached).length) return Promise.resolve(cached);
  return fetchState(site, userId);
};

const getState = async (site: Site, userId: UserId): Promise<GettingStartedGuideState> => {
  const rawState = await getRawState(site, userId);

  return transformState(rawState, site);
};

// eslint-disable-next-line
const setState = async (
  key: GettingStartedGuideStateEnum,
  value: string | boolean,
  site: Site,
  userId: UserId,
): Promise<GettingStartedGuideState> => {
  const scopedKeyValue = { namespace: NAMESPACE, key, value };
  const targeting = { siteId: site.id };

  await setAppState(userId, scopedKeyValue, targeting);

  const cachedSiteState = store.getState()[site.id];

  const state = {
    ...cachedSiteState,
    [key]: value,
  };

  if (cachedSiteState) {
    store.setState({
      [site.id]: state,
    });

    return transformState(store.getState()[site.id], site);
  }

  return getState(site, userId);
};

const updateStepState = async (
  key: GettingStartedGuideStateEnum,
  site: Site,
  userId: UserId,
): Promise<GettingStartedGuideState> => {
  try {
    const state = await getRawState(site, userId);

    if (!state[key]) {
      const newState = await setState(key, true, site, userId);
      return newState;
    }

    return transformState(state, site);
  } catch {
    return DEFAULT_STATE;
  }
};

export const refreshStepState = async (
  site: Site,
  userId: UserId,
): Promise<GettingStartedGuideState> => {
  const state = await getState(site, userId);

  return state;
};

export const markStepAsCompleted = async (
  stepId: StepId,
  site: Site | undefined,
  userId: UserId | undefined,
): Promise<GettingStartedGuideState> => {
  if (!site || !userId) {
    return DEFAULT_STATE;
  }

  switch (stepId) {
    case StepId.Welcome:
      return updateStepState(GettingStartedGuideStateEnum.SAW_WELCOME_VIDEO, site, userId);
    case StepId.Invite:
      return updateStepState(GettingStartedGuideStateEnum.INVITE_COMPLETED_KEY, site, userId);
    case StepId.Recording:
      return updateStepState(GettingStartedGuideStateEnum.VIEWED_RECORDING_KEY, site, userId);
    case StepId.Heatmap:
      return updateStepState(GettingStartedGuideStateEnum.VIEWED_HEATMAP_KEY, site, userId);
    case StepId.Feedback:
      return updateStepState(
        GettingStartedGuideStateEnum.CREATED_INCOMING_FEEDBACK_KEY,
        site,
        userId,
      );
    case StepId.Survey:
      return updateStepState(GettingStartedGuideStateEnum.LAUNCHED_SURVEY_KEY, site, userId);
    case StepId.Highlights:
      return updateStepState(GettingStartedGuideStateEnum.HIGHLIGHTS_VIEWED, site, userId);
    case StepId.Integrations:
      return updateStepState(GettingStartedGuideStateEnum.INTEGRATIONS_VIEWED, site, userId);
    case StepId.Segments:
      return updateStepState(GettingStartedGuideStateEnum.SEGMENTS_VIEWED, site, userId);
    case StepId.GlobalFilters:
      return updateStepState(GettingStartedGuideStateEnum.GLOBAL_FILTERS_VIEWED, site, userId);
    case StepId.LocalFilters:
      return updateStepState(GettingStartedGuideStateEnum.LOCAL_FILTERS_VIEWED, site, userId);

    default:
      return getState(site, userId);
  }
};

export const complete = (site: Site | undefined, userId: UserId | undefined): void => {
  if (!site || !userId) {
    return;
  }

  setState(GettingStartedGuideStateEnum.COMPLETED_KEY, true, site, userId);
};

export const markAsCelebrated = (site: Site | undefined, userId: UserId | undefined): void => {
  if (!site || !userId) {
    return;
  }

  setState(GettingStartedGuideStateEnum.CELEBRATED_KEY, true, site, userId);
};

export const markInviteAsCompleted = (
  site: Site,
  userId: UserId,
): Promise<GettingStartedGuideState> => {
  return updateStepState(GettingStartedGuideStateEnum.INVITE_COMPLETED_KEY, site, userId);
};

export const markIncomingFeedbackAsCompleted = (
  site: Site,
  userId: UserId,
): Promise<GettingStartedGuideState> => {
  return updateStepState(GettingStartedGuideStateEnum.CREATED_INCOMING_FEEDBACK_KEY, site, userId);
};

export const markSurveyAsCompleted = (
  site: Site,
  userId: UserId,
): Promise<GettingStartedGuideState> => {
  return updateStepState(GettingStartedGuideStateEnum.LAUNCHED_SURVEY_KEY, site, userId);
};

export const markHeatmapAsSeen = (
  site: Site,
  userId: UserId,
): Promise<GettingStartedGuideState> => {
  return updateStepState(GettingStartedGuideStateEnum.VIEWED_HEATMAP_KEY, site, userId);
};

export const markRecordingAsSeen = (
  site: Site | undefined,
  userId: UserId | undefined,
): Promise<GettingStartedGuideState> => {
  if (!site || !userId) {
    return Promise.resolve(DEFAULT_STATE);
  }

  return updateStepState(GettingStartedGuideStateEnum.VIEWED_RECORDING_KEY, site, userId);
};

export const markSpacesStepAsCompleted = (
  site: Site | undefined,
  userId: UserId | undefined,
): Promise<GettingStartedGuideState> => {
  if (!site || !userId) return Promise.resolve(DEFAULT_STATE);

  return updateStepState(GettingStartedGuideStateEnum.SPACES_STEP_COMPLETED_KEY, site, userId);
};

const updateInviteProgress = async (site: Site, userId: UserId) => {
  const { users } = site.organization;

  if (users?.length > 1) {
    markInviteAsCompleted(site, userId);
  }
};

export const init = async (
  site: Site | undefined,
  userId: UserId | undefined,
): Promise<GettingStartedGuideState> => {
  if (!site || !userId) {
    return DEFAULT_STATE;
  }

  if (site.created < SITE_CREATED_TIMESTAMP) {
    return updateStepState(GettingStartedGuideStateEnum.COMPLETED_KEY, site, userId);
  }

  const storedState = store.getState()[site.id];

  if (storedState) {
    return transformState(storedState, site);
  }

  try {
    const state = await getState(site, userId);
    if (state.isInviteEnabled) await updateInviteProgress(site, userId);

    return refreshStepState(site, userId);
  } catch {
    return DEFAULT_STATE;
  }
};
